import { useAccount, useReadContract, useReadContracts } from "wagmi";
import { formatUnits, type Address } from "viem";
import { erc20ABI, mbcTokenABI, usdtTokenABI } from "@/constants/abi";

/**
 * Custom hook to fetch token balance by symbol using wagmi
 * Uses wagmi's built-in:
 * - Request deduplication (multiple components share same request)
 * - Automatic caching
 * - Background refetching
 * - Error handling and retry logic
 *
 * @param tokenSymbol - Token symbol (e.g., "USDT", "MBC")
 * @param refreshKey - Trigger re-fetch when this value changes (unused, kept for API compatibility)
 * @returns Formatted token balance as string
 */
function useTokenBalance(tokenSymbol: string, refreshKey: number = 0) {
  const { address: walletAddress } = useAccount();
  const tokenContractAddress = import.meta.env[`VITE_${tokenSymbol}_ADDRESS`] as Address | undefined;

  // Fetch balance and decimals in parallel using useReadContracts
  const { data, isLoading, isError, refetch } = useReadContracts({
    contracts: [
      {
        address: tokenContractAddress,
        abi: erc20ABI,
        functionName: "balanceOf",
        args: walletAddress ? [walletAddress] : undefined,
      },
      {
        address: tokenContractAddress,
        abi: erc20ABI,
        functionName: "decimals",
      },
    ],
    query: {
      enabled: !!walletAddress && !!tokenContractAddress,
      staleTime: 5000,
      refetchInterval: 10000,
      refetchOnWindowFocus: true,
      retry: 2,
      retryDelay: 1000,
    },
  });

  // Extract and format balance
  const balance = data?.[0]?.result;
  const decimals = data?.[1]?.result ?? 18;

  const formattedBalance = balance !== undefined ? formatUnits(balance, decimals) : "0";

  return formattedBalance;
}

/**
 * Custom hook to fetch balances for multiple tokens (USDT, MBC, ENT)
 * Uses wagmi's useReadContracts for batched RPC calls
 * @returns Object with usdt, mbc, ent balances as strings
 */
export function useAllTokenBalances() {
  const { address: walletAddress } = useAccount();

  const usdtAddress = import.meta.env.VITE_USDT_ADDRESS as Address | undefined;
  const mbcAddress = import.meta.env.VITE_MBC_ADDRESS as Address | undefined;
  const entAddress = import.meta.env.VITE_ENT_ADDRESS as Address | undefined;

  const { data, isLoading, isError, refetch } = useReadContracts({
    contracts: [
      // USDT balance and decimals
      {
        address: usdtAddress,
        abi: usdtTokenABI,
        functionName: "balanceOf",
        args: walletAddress ? [walletAddress] : undefined,
      },
      {
        address: usdtAddress,
        abi: usdtTokenABI,
        functionName: "decimals",
      },
      // MBC balance and decimals
      {
        address: mbcAddress,
        abi: mbcTokenABI,
        functionName: "balanceOf",
        args: walletAddress ? [walletAddress] : undefined,
      },
      {
        address: mbcAddress,
        abi: mbcTokenABI,
        functionName: "decimals",
      },
      // ENT balance and decimals
      {
        address: entAddress,
        abi: erc20ABI,
        functionName: "balanceOf",
        args: walletAddress ? [walletAddress] : undefined,
      },
      {
        address: entAddress,
        abi: erc20ABI,
        functionName: "decimals",
      },
    ],
    query: {
      enabled: !!walletAddress,
      staleTime: 5000,
      refetchInterval: 1000000,
      refetchOnWindowFocus: true,
      retry: 2,
      retryDelay: 1000,
    },
  });

  // Extract and format balances
  const usdtBalance = data?.[0]?.result;
  const usdtDecimals = data?.[1]?.result ?? 18;
  const mbcBalance = data?.[2]?.result;
  const mbcDecimals = data?.[3]?.result ?? 18;
  const entBalance = data?.[4]?.result;
  const entDecimals = data?.[5]?.result ?? 18;

  return {
    isLoading,
    isError,
    refetch,
    usdt: usdtBalance !== undefined ? formatUnits(usdtBalance, usdtDecimals) : "0",
    mbc: mbcBalance !== undefined ? formatUnits(mbcBalance, mbcDecimals) : "0",
    ent: entBalance !== undefined ? formatUnits(entBalance, entDecimals) : "0",
  };
}

export default useTokenBalance;
